import { useNavigate } from "react-router-dom"
import { Sheet, SheetContent } from "../../Components/UI/sheet"
import {
  Users, ClipboardList, Settings, X, ListTodo, ChevronDown, ChevronRight, LogOut, User, Database, Shield, Puzzle, Globe, TrendingUp, ReceiptText,
  ArrowRight, Banknote, FileText, BanknoteArrowDown,
  ChartColumn, HeadphonesIcon, Funnel, ShoppingCart, HelpCircle, GraduationCap, AlertTriangle, Lightbulb, Building2, Smartphone
} from "lucide-react"
import { BsCartFill } from "react-icons/bs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faFilterCircleDollar } from "@fortawesome/free-solid-svg-icons"
import { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import UsersPage from "../../Modules/Settings/SubSettings/Users"
import MastersPage from "../../Modules/Settings/SubSettings/Masters"
import PoliciesPage from "../../Modules/Settings/SubSettings/Policies"
import IntegrationsPage from "../../Modules/Settings/SubSettings/Integrations"
import SubscriptionInfo from "../../Modules/Settings/SubscriptionInfo/SubscriptionInfo"
import UpgradeSubscription from "../../Modules/Settings/UpgradeSubscription/UpgradeSubscription"
import Upcoming from "../../Components/Common/Upcoming"
import { useDesktop } from "../../Components/Common/IsDesktop";
import { getActiveThreadTypesArr, userIsAdmin, checkIfInqOnlyMode, getThreadTypesArrForSC, isAgencyAllowed } from "../../Store/Reducers"
import { checkUserRights, getThreadTypeMeta, isIOSDevice } from "../../Utils/Utilities" // Update with correct path
import { clearEverything } from "../../Utils/CookieHandler"
import { AlertBox } from "../../Components/Common/AlertBox"
import WebStoreConfig from "../../Modules/Settings/WebStoreConfig/WebStoreConfig"
import { getAuthData } from "../../Utils/AuthUtils"
import apiService from "../../Services/APIService"
import { useModalBackButton } from "../../Hooks/useModalBackButton"
import { isSmartSellerMode } from "../../Store/Reducers"
import FeedbackDialog from "../../Modules/Ticket/TktDialog"
import QuickTraining from "../QuickTraining/QuickTraining"
import { getTrainingData } from "../QuickTraining/QuickTrainingData"
import { canShowImportInquiries, SHOW_EXPENSES, SHOW_LISTING } from "../../Utils/FeatureFlags";
import { hasFeatureAccess } from "../../Utils/Constants";

// FontAwesome icon wrapper component to match Lucide icon structure
const FilterCircleDollarIcon = (props) => (
  <FontAwesomeIcon
    icon={faFilterCircleDollar}
    {...props}
    style={{
      fill: 'none',
    }}
  />
);

const MenuDrawer = ({ isOpen, onClose }) => {
  const navigate = useNavigate()
  const [expandedMenus, setExpandedMenus] = useState({})
  const [currentSettingPage, setCurrentSettingPage] = useState(null)
  const [showUpcoming, setShowUpcoming] = useState(false)
  const [openModel, setOpenModel] = useState({ modelID: '' })
  const [showIOSAlert, setShowIOSAlert] = useState(false)
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false)
  const [selectedFeedbackType, setSelectedFeedbackType] = useState(null)
  const [isQuickTrainingOpen, setIsQuickTrainingOpen] = useState(false)

  const { data: agencyData } = useSelector(state => state.agencyData);
  const maxUsers = agencyData?.CntMaxUsers || 0;

  const isDesktop = useDesktop();
  const isAdmin = useSelector(userIsAdmin);
  const isInqOnlyMode = useSelector(checkIfInqOnlyMode);
  const userRights = checkUserRights();

  // Handle mobile back button to close drawer
  useModalBackButton(isOpen, onClose, 'menu-drawer');

  useEffect(() => {
    if (isDesktop) {
      handleClose();
    }
  }, [isDesktop])

  useEffect(() => {
    if (isOpen) {
      setExpandedMenus({
        connections: true,
        threads: true,
        settings: true
      });
    }
  }, [isOpen])

  // Settings configuration moved here
  const settingsConfig = [
    {
      id: 'users',
      icon: User,
      title: 'Users',
      description: 'Manage system users and roles',
      component: UsersPage,
      show: true
    },
    {
      id: 'masters',
      icon: Database,
      title: 'Masters',
      description: 'Configure master data lists',
      component: MastersPage,
      show: true
    },
    {
      id: 'policies',
      icon: Shield,
      title: 'Policies',
      description: 'Set business rules and controls',
      component: PoliciesPage,
      show: true
    },
    {
      id: 'integrations',
      icon: Puzzle,
      title: 'Integrations',
      description: 'Connect external platforms',
      component: IntegrationsPage,
      show: canShowImportInquiries()
    },
    {
      id: 'upgrade',
      icon: TrendingUp,
      title: isIOSDevice() ? 'Manage Subscription' : 'Upgrade',
      description: isIOSDevice() ? 'Manage your subscription' : 'Upgrade your subscription',
      component: UpgradeSubscription,
      show: true
    },
    {
      id: 'subscriptionInfo',
      icon: ReceiptText,
      title: 'Subscription Information',
      description: 'See all your subscription history',
      component: SubscriptionInfo,
      show: Number(agencyData?.SubTypeID) === 2
    },
    {
      id: 'webstoreConfig',
      icon: Globe,
      title: 'Webstore',
      description: 'Settings for your webstore',
      component: null,
      show: true
    }
  ]

  const activeThreadTypes = useSelector(getActiveThreadTypesArr);
  const allThreadTypes = useSelector(getThreadTypesArrForSC);
  const isAgencyAuthorized = useSelector(isAgencyAllowed);


  const threadTypes = isAgencyAuthorized ? allThreadTypes : activeThreadTypes;


  // Create thread submenu items from the determined threadTypes source
  const threadSubItems = threadTypes?.map(thread => {
    const threadMeta = getThreadTypeMeta(thread.TID, threadTypes)
    return {
      id: `thread-${thread.TID}`,
      label: thread.Typ,
      icon: threadMeta.Icon,
      colorClass: threadMeta.colorClass,
      path: `/threads?tid=${thread.TID}`,
      isThreadItem: true
    }
  }) || []

  const authData = getAuthData();
  const userName = authData?.UserName || authData?.name || authData?.Name || "User";
  const businessName = agencyData?.AgencyName || authData?.AgencyName || authData?.agencyName || "Business";

  // Sectionized menu structure with Sahi CRM color themes
  const menuSections = [
    {
      title: "Growth",
      items: [
        {
          id: "connections",
          label: "Contacts",
          icon: Users,
          path: "/connections",
          iconColor: "text-sahi-orange",
          iconBg: "bg-sahi-orange-light text-sahi-orange",
          show: true
        },
        ...(isAgencyAuthorized
          ? [
            {
              id: "threads",
              label: "Threads",
              icon: ClipboardList,
              path: "/threads",
              subItems: threadSubItems,
              iconColor: "text-sahi-orange",
              iconBg: "bg-sahi-orange-light text-sahi-orange",
              show: true
            }
          ]
          : [
            {
              id: "threads",
              label: isInqOnlyMode ? "Inquiries" : "Threads",
              icon: isInqOnlyMode ? Funnel : ClipboardList,
              path: isInqOnlyMode ? "/threads?tid=1" : "/threads",
              subItems: isInqOnlyMode ? [] : threadSubItems,
              iconColor: "text-sahi-orange",
              iconBg: "bg-sahi-orange-light text-sahi-orange",
              show: true
            },
            {
              id: "orders",
              label: "Sales",
              icon: ShoppingCart,
              path: `threads-report?tID=2&m=${new Date().getMonth() + 1}`,
              iconColor: "text-sahi-orange",
              iconBg: "bg-sahi-orange-light text-sahi-orange",
              show: isInqOnlyMode
            }
          ]
        ),
        {
          id: "transactions",
          label: "Transactions",
          icon: Banknote,
          path: "/transactions",
          iconColor: "text-sahi-orange",
          iconBg: "bg-sahi-orange-light text-sahi-orange",
          show: !!agencyData?.EnblXns
        },
        {
          id: "contracts",
          label: "Contracts",
          icon: BanknoteArrowDown,
          path: "/contracts",
          iconColor: "text-sahi-orange",
          iconBg: "bg-sahi-orange-light text-sahi-orange",
          show: !!agencyData?.EnblContracts
        },
        {
          id: "expenses",
          label: "Expenses",
          icon: BanknoteArrowDown,
          path: "/expenses",
          iconColor: "text-sahi-orange",
          iconBg: "bg-sahi-orange-light text-sahi-orange",
          show: SHOW_EXPENSES() && userRights.agnConfig
        }
      ].filter(item => item.show)
    },
    {
      title: "Reports",
      items: [
        {
          id: "reports",
          label: "Reports",
          icon: ChartColumn,
          path: "/reports",
          iconColor: "text-sahi-dark-blue",
          iconBg: "bg-sahi-dark-blue-light text-sahi-dark-blue",
          show: true
        }
      ].filter(item => item.show)
    },
    {
      title: "Network",
      items: [
        {
          id: "community",
          label: "Network",
          icon: Globe,
          path: "/community",
          iconColor: "text-sahi-green",
          iconBg: "bg-sahi-green-light text-sahi-green",
          show: hasFeatureAccess("community")
        },
        {
          id: "listing",
          label: "Directory",
          icon: Building2,
          path: "/directory",
          iconColor: "text-sahi-green",
          iconBg: "bg-sahi-green-light text-sahi-green",
          show: SHOW_LISTING()
        }
      ].filter(item => item.show)
    },
    {
      title: "Setup",
      items: [
        {
          id: "users-menu",
          label: "Users",
          icon: User,
          path: "/users",
          iconColor: "text-zinc-700",
          iconBg: "bg-zinc-100 text-zinc-700",
          show: isAdmin && !isSmartSellerMode()
        },
        {
          id: "settings",
          label: "Settings",
          icon: Settings,
          path: "/settings",
          iconColor: "text-zinc-700",
          iconBg: "bg-zinc-100 text-zinc-700",
          show: isAdmin
        },
        ...(String(agencyData?.AgencyID || localStorage.getItem('Jarvis'))?.includes('41415') ? [{
          id: "haptics",
          label: "Haptics Test",
          icon: Smartphone,
          path: "/haptics",
          iconColor: "text-emerald-600",
          iconBg: "bg-emerald-50 text-emerald-600",
          show: true
        }] : [])
      ].filter(item => item.show)
    },
    {
      title: "Support",
      items: [
        {
          id: "support-tickets-report",
          label: "Support Tickets",
          icon: HeadphonesIcon,
          path: `/threads-report?tID=3&m=${new Date().getMonth() + 1}`,
          iconColor: "text-sahi-red",
          iconBg: "bg-sahi-red-light text-sahi-red",
          show: [1].includes(Number(agencyData?.AgencyID))
        },
        {
          id: "share-idea",
          label: "Share Idea",
          icon: Lightbulb,
          isSupportItem: true,
          supportType: 'idea',
          iconColor: "text-sahi-red",
          iconBg: "bg-sahi-red-light text-sahi-red",
          show: true
        },
        {
          id: "bug-report",
          label: "Report Bug",
          icon: AlertTriangle,
          isSupportItem: true,
          supportType: 'bug',
          iconColor: "text-sahi-red",
          iconBg: "bg-sahi-red-light text-sahi-red",
          show: true
        }
      ].filter(item => item.show)
    }
  ].filter(sec => sec.items.length > 0)

  const handleMenuItemClick = (path) => {
    navigate(path)
    onClose()
  }

  const handleLogout = async () => {
    try {
      const authData = getAuthData();

      if (!authData) {
        throw new Error('No authentication data found');
      }

      const result = await apiService.accessSetData(2, 'logout', {});

      // Parse the response if it's a string
      const parsedResult = typeof result.data === 'string' ? JSON.parse(result.data) : result.data;

      // Check for error
      if (parsedResult?.Table && parsedResult.Table[0]?.FnErrCode !== '-1') {
        throw new Error(parsedResult.Table[0]?.ErrMsg || 'Logout failed');
      }
      clearEverything();

      return {
        success: true,
        message: 'Logged out.'
      };
    } catch (error) {
      console.error('Error logging out:', error);
      throw error;
    }
  };

  const handleClose = () => {
    setExpandedMenus({})
    setCurrentSettingPage(null)
    setShowUpcoming(false)
    setOpenModel({ modelID: '' })
    setIsFeedbackOpen(false)
    setSelectedFeedbackType(null)
    setIsQuickTrainingOpen(false)
    onClose()
  }

  const handleSupportClick = (id) => {
    if (id === 'training') {
      setIsQuickTrainingOpen(true)
    } else if (id === 'bug') {
      setSelectedFeedbackType(1) // 1 = Technical Issue/Bug
      setIsFeedbackOpen(true)
    } else if (id === 'idea') {
      setSelectedFeedbackType(2) // 2 = Feature Suggestion/Idea
      setIsFeedbackOpen(true)
    }
  }

  const handleFeedbackClose = () => {
    setIsFeedbackOpen(false)
    setSelectedFeedbackType(null)
  }

  const handleSettingClick = (settingId) => {
    const setting = settingsConfig.find(s => s.id === settingId)

    if (!setting) return

    // Handle special cases
    if (settingId === 'users') {
      navigate('/users')
      onClose()
      return
    }
    else if (settingId === 'integrations') {
      setCurrentSettingPage(settingId);
      return;
    }
    else if (settingId === 'webstoreConfig') {
      setOpenModel({ modelID: 'webstoreConfig' })
      return
    }
    else if (settingId === 'subscriptionInfo') {
      setOpenModel({ modelID: 'subscriptionInfo' })
      return
    }
    else if (settingId === 'upgrade') {
      if (isIOSDevice()) {
        setShowIOSAlert(true)
        return
      }
      setOpenModel({ modelID: 'upgrade' })
      return
    }

    // For components that should open in full screen
    if (setting.component) {
      setCurrentSettingPage(settingId)
    }
  }

  const toggleSubmenu = (itemId) => {
    setExpandedMenus((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }))
  }


  const handleBackToSettings = () => {
    setCurrentSettingPage(null)
  }

  // If a setting page is selected, render it full screen
  if (currentSettingPage) {
    const setting = settingsConfig.find(s => s.id === currentSettingPage)
    if (setting && setting.component) {
      const SettingComponent = setting.component

      return (
        <Sheet open={isOpen} onOpenChange={onClose}>
          <SheetContent
            side="right"
            className="w-full max-w-full p-0 border-0 shadow-none"
            closeIcon={false}
            style={{ boxShadow: 'none', border: 'none' }}
          >
            <div className="h-full flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between p-4 bg-white border-b border-sahi-border-color">
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleClose}
                    className=" hover:bg-sahi-light-grey p-1 rounded"
                  >
                    <ArrowRight className="w-5 h-5 rotate-180" />
                  </button>
                  <h2 className="text-lg font-semibold">{setting.title}</h2>
                </div>
              </div>

              {/* Setting Component */}
              <div className="flex-1 overflow-y-auto p-4 bg-bg-grey">
                <SettingComponent
                  onNavigateBack={handleBackToSettings}
                  onClose={onClose}
                />
              </div>
            </div>
          </SheetContent>
        </Sheet>
      )
    }
  }

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="right" className="min-w-72 sm:min-w-80 p-0 border-l border-zinc-100 bg-white" closeIcon={false}>
        <div className="h-full flex flex-col bg-white">
          {/* Header with User Name and Agency Name */}
          <div className="flex items-start justify-between px-5 py-4 border-b border-zinc-100">
            <div className="flex items-center gap-3 min-w-0 pr-2">
              <div className="min-w-0">
                <div className="text-md font-semibold text-sahi-green">
                  Hi, {userName}
                </div>
                <div className="text-xs font-medium text-zinc-500 pt-0.5 truncate" title={businessName}>
                  {businessName}
                </div>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-1.5 rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors flex-shrink-0 mt-0.5"
              aria-label="Close menu"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Menu Items with Grouped Sections */}
          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-4">
            {menuSections.map((section, sIndex) => (
              <div key={section.title || sIndex} className="space-y-1">
                {/* Section Header */}
                <div className="px-2.5 pt-1 pb-0 text-[11px] font-medium text-zinc-500">
                  {section.title}
                </div>

                <ul className="space-y-0">
                  {section.items.map((item) => {
                    const Icon = item.icon
                    const isExpanded = expandedMenus[item.id]
                    const hasSubItems = (item.subItems && item.subItems.length > 0) || item.useComponent

                    const handleItemClick = () => {
                      if (hasSubItems) {
                        toggleSubmenu(item.id)
                      } else if (item.isSupportItem) {
                        handleSupportClick(item.supportType)
                      } else {
                        handleMenuItemClick(item.path)
                      }
                    }

                    return (
                      <li key={item.id}>
                        <div className="space-y-0">
                          <button
                            onClick={handleItemClick}
                            className="w-full flex items-center gap-3 px-2.5 py-2 rounded-xl hover:bg-zinc-50/80 active:bg-zinc-100 transition-all duration-200 group text-left"
                          >
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${item.iconBg || 'bg-zinc-50'} group-hover:scale-105 transition-transform duration-200 flex-shrink-0`}>
                              <Icon className={`w-4 h-4 ${item.iconColor || 'text-zinc-700'}`} />
                            </div>
                            <span className="text-zinc-800 text-[14px] font-medium group-hover:text-zinc-950 transition-colors flex-1">
                              {item.label}
                            </span>
                            {hasSubItems && (
                              <div className="transition-transform duration-200 text-zinc-400 group-hover:text-zinc-600">
                                {isExpanded ? (
                                  <ChevronDown className="w-4 h-4" />
                                ) : (
                                  <ChevronRight className="w-4 h-4" />
                                )}
                              </div>
                            )}
                          </button>

                          {hasSubItems && isExpanded && (
                            <div className="ml-10 border-l-2 border-zinc-100 pl-2">
                              <ul className="space-y-0">
                                {item.subItems?.map((subItem) => {
                                  const SubIcon = subItem.icon
                                  return (
                                    <li key={subItem.id}>
                                      <button
                                        onClick={() => {
                                          if (subItem.isSettingItem) {
                                            handleSettingClick(subItem.id)
                                          } else if (subItem.isSupportItem) {
                                            handleSupportClick(subItem.id)
                                          } else if (subItem.isThreadItem) {
                                            handleMenuItemClick(subItem.path)
                                          } else {
                                            handleMenuItemClick(subItem.path)
                                          }
                                        }}
                                        className="w-full flex items-center gap-2.5 px-2 py-1 rounded-lg hover:bg-zinc-50 transition-colors duration-200 group text-left"
                                      >
                                        <SubIcon className={`w-3.5 h-3.5 flex-shrink-0 ${subItem?.colorClass || 'text-zinc-500'} group-hover:scale-110 transition-transform duration-200`} />
                                        <span className="text-zinc-600 text-[13px] font-normal group-hover:text-zinc-900 transition-colors">
                                          {subItem.label}
                                        </span>
                                      </button>
                                    </li>
                                  )
                                })}
                              </ul>
                            </div>
                          )}
                        </div>
                      </li>
                    )
                  })}
                </ul>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-zinc-100 bg-zinc-50/50">
            <div className="flex items-center justify-end">
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 py-1 rounded-lg text-red-600 hover:bg-red-50 active:bg-red-100/70 transition-colors duration-200 group"
                title="Logout"
              >
                <LogOut className="w-4 h-4 text-red-500 group-hover:scale-110 transition-transform duration-200" />
                <span className="text-xs font-semibold">Logout</span>
              </button>
            </div>
          </div>
        </div>

        {/* Modals */}
        <Upcoming
          open={showUpcoming}
          onOpenChange={(open) => {
            setShowUpcoming(open)
          }}
        />

        <SubscriptionInfo
          open={openModel.modelID === 'subscriptionInfo'}
          onClose={() => setOpenModel({ modelID: '' })}
        />

        <UpgradeSubscription
          open={openModel.modelID === 'upgrade'}
          onClose={() => setOpenModel({ modelID: '' })}
        />

        <WebStoreConfig
          open={openModel.modelID === 'webstoreConfig'}
          onClose={() => setOpenModel({ modelID: '' })}
        />

        <AlertBox
          open={showIOSAlert}
          onOpenChange={setShowIOSAlert}
          title="Manage Subscription"
          description="You can manage your subscription by logging into the desktop view."
          actions={[
            {
              label: "OK",
              value: "ok",
              variant: "default",
              className: "bg-sahi-primary-color text-white hover:bg-sahi-primary-color/90"
            }
          ]}
          onAction={() => setShowIOSAlert(false)}
        />

        <QuickTraining
          isOpen={isQuickTrainingOpen}
          onClose={() => setIsQuickTrainingOpen(false)}
          trainingData={getTrainingData("TRAINING1")}
        />

        <FeedbackDialog
          isOpen={isFeedbackOpen}
          onClose={handleFeedbackClose}
          selectedFeedbackType={selectedFeedbackType}
        />
      </SheetContent>
    </Sheet>
  )
}

export default MenuDrawer